import * as React from 'react';

interface BlinkState {
  circuits: string[];
  active: boolean;
}

interface SimulationState {
  circuits: string[];
  running: boolean;
  mode: 'on' | 'off';
}

interface LightingEffectsContextValue {
  blink: BlinkState;
  sim: SimulationState;
  startBlink: (circuits: string[]) => void;
  stopBlink: () => void;
  startSim: (circuits: string[], mode?: 'on' | 'off') => void;
  stopSim: () => void;
  addToSim: (circuits: string[]) => void;
  removeFromSim: (circuits: string[]) => void;
}

const LightingEffectsContext = React.createContext<LightingEffectsContextValue | undefined>(
  undefined,
);

export const LightingEffectsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [blink, setBlink] = React.useState<BlinkState>({ circuits: [], active: false });
  const [sim, setSim] = React.useState<SimulationState>({
    circuits: [],
    running: false,
    mode: 'on',
  });

  const startBlink = React.useCallback((circuits: string[]) => {
    setBlink({ circuits, active: true });
  }, []);

  const stopBlink = React.useCallback(() => {
    setBlink({ circuits: [], active: false });
  }, []);

  const startSim = React.useCallback((circuits: string[], mode: 'on' | 'off' = 'on') => {
    setSim({ circuits, running: true, mode });
  }, []);

  const stopSim = React.useCallback(() => {
    setSim({ circuits: [], running: false, mode: 'on' });
  }, []);

  const addToSim = React.useCallback((circuits: string[]) => {
    setSim(prev => ({
      ...prev,
      circuits: [...new Set([...prev.circuits, ...circuits])], // Add unique circuits
      running: true,
      mode: 'on',
    }));
  }, []);

  const removeFromSim = React.useCallback((circuits: string[]) => {
    setSim(prev => {
      const remaining = prev.circuits.filter(c => !circuits.includes(c));
      return {
        ...prev,
        circuits: remaining,
        running: remaining.length > 0, // Stop simulation if no circuits left
        mode: prev.mode,
      };
    });
  }, []);

  return (
    <LightingEffectsContext.Provider
      value={{ blink, sim, startBlink, stopBlink, startSim, stopSim, addToSim, removeFromSim }}
    >
      {children}
    </LightingEffectsContext.Provider>
  );
};

export const useLightingEffects = () => {
  const ctx = React.useContext(LightingEffectsContext);
  if (!ctx) throw new Error('useLightingEffects must be used within LightingEffectsProvider');
  return ctx;
};
